import { useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { findOwningAgentId } from "./ownership.js";
import { useInviteCardsList } from "./useInviteCardsList.js";
import { useOwningAgentProfile } from "./useOwningAgentProfile.js";
import { createInviteCard, revokeInviteCard } from "./inviteCardsApi.js";
import CopyLinkField from "./CopyLinkField.jsx";
// P156: extracted so the Employer Invite Card screen reads the exact same
// status/usage/expiry logic instead of a second copy that would drift.
import {
  LINK_HIDDEN_FOR_ROLE_TEXT,
  STATUS_FILTERS,
  formatDateTh,
  formatTarget,
  usageNote,
  formatUsage,
  MetricCard,
  PublicUrlCell,
  StatusCell,
  ExpiresCell,
  STATUS_BADGE_VARIANT,
} from "./inviteCardShared.jsx";
import "./agentInviteCards.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Invite Card"];
const DRAWER_TITLE_ID = "ic-drawer-title";

// Fixed, generic message only - mirrors the backend's own "never leak the
// configured URL" rule (Prompt 089R). Never renders error.message from a
// 500 response, even though the backend's own message is already safe -
// this is the frontend's independent guarantee.
const SAFE_CONFIG_ERROR_MESSAGE =
  "ไม่สามารถสร้างหรือแสดงลิงก์ Invite Card ได้ในขณะนี้เนื่องจากปัญหาการตั้งค่าระบบ กรุณาติดต่อฝ่ายปฏิบัติการ";
// Presentational option set matching the W43 create-drawer reference
// (Bangladesh/Laos/Myanmar/Cambodia, Bangla/Lao/Burmese/Khmer/Thai/English) -
// not fabricated business data, just the selectable values the approved design
// shows. The backend only stores whatever short string is submitted (max 10
// chars), so these values double as the table's real "BD / Bangla"-style
// display once saved.
//
// "Bangla", not "Bengali": the workers this is for are Bangladeshi and call
// their language Bangla; "Bengali" is the English exonym that also points at
// West Bengal in India. The app was renamed to Bangla everywhere (the mobile
// interpreter's M40 change), and the create form must not keep offering the old
// name. Legacy cards stored as "bn"/"Bengali" still read "Bangla" in the table
// via formatLanguage().
const COUNTRY_OPTIONS = [
  { value: "BD", label: "Bangladesh" },
  { value: "LA", label: "Laos" },
  { value: "MM", label: "Myanmar" },
  { value: "KH", label: "Cambodia" },
];

const LANGUAGE_OPTIONS = [
  { value: "Bangla", label: "Bangla" },
  { value: "Lao", label: "Lao" },
  { value: "Burmese", label: "Burmese" },
  { value: "Khmer", label: "Khmer" },
  { value: "Thai", label: "Thai" },
  { value: "English", label: "English" },
];

function matchesSearch(card, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [card.inviteCodeMasked, card.intendedCountryCode, card.intendedLanguageCode, card.noteToWorker].some(
    (field) => String(field ?? "").toLowerCase().includes(needle)
  );
}

function describeCreateError(error) {
  if (error?.status === 500) {
    return SAFE_CONFIG_ERROR_MESSAGE;
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้าง Invite Card สำหรับ Agent นี้";
  }
  if (error?.status === 400) {
    return error.message?.replace(/^Request failed with status \d+$/, "") || "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถสร้าง Invite Card ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/agent/invite-cards - the Agent Portal's Invite Card management
 * screen (Prompt 090, built from the W43 reference). This is the only route
 * where Invite Card creation is actually enabled: agentId is derived
 * entirely from the signed-in actor's own accessScopes (never a route
 * param), so there is no way to reach another Agent's cards or to land here
 * as SuperAdmin/Super Agent/PLC and see a create button - those roles never
 * have an "Agent" + "Agent" scope row to resolve.
 */
export default function AgentInviteCardsPage() {
  const { status: identityStatus, data: identity, error: identityError } = useBackofficeIdentity();
  const agentId = identityStatus === "ready" ? findOwningAgentId(identity.accessScopes) : null;

  const { status: listStatus, data: listData, error: listError, refetch } = useInviteCardsList(agentId);
  const { data: profile } = useOwningAgentProfile(agentId);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [drawer, setDrawer] = useState(null); // null | { mode: "create" } | { mode: "view", card }

  // Prompt 138Z-A: revoked cards are excluded right here, at the source -
  // every other derived value (filteredItems, metrics, the empty-state
  // check) reads from this same `items`, so a revoked card can never leak
  // into the operational list through any code path, "All" filter
  // included. Backend still keeps the revoked record for audit history -
  // this is a display-only exclusion, never a delete.
  const items = useMemo(() => (listData?.items ?? []).filter((card) => card.status !== "Revoked"), [listData]);
  const filteredItems = items.filter(
    (card) => (statusFilter === "All" || card.status === statusFilter) && matchesSearch(card, query)
  );
  const metrics = useMemo(
    () => ({
      active: items.filter((card) => card.status === "Active").length,
      expired: items.filter((card) => card.status === "Expired").length,
    }),
    [items]
  );

  function closeDrawer() {
    setDrawer(null);
  }

  function handleCreated() {
    refetch();
  }

  // Prompt 138Z-A: closes the drawer instead of re-opening it on the
  // just-revoked card - a revoked card is excluded from `items` (see
  // above), so leaving the drawer open on it would show a red/revoked row
  // on screen even though the list behind it no longer has one. refetch()
  // re-pulls the real list so the revoked card disappears the moment the
  // drawer closes.
  function handleRevoked() {
    refetch();
    closeDrawer();
  }

  if (identityStatus === "loading") {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="cards" label="กำลังโหลดข้อมูลผู้ใช้งาน..." />
      </AppShell>
    );
  }

  if (identityStatus === "error") {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดข้อมูลผู้ใช้งานได้</h1>
          <p className="wx-state-body">
            {identityError?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ"}
          </p>
        </div>
      </AppShell>
    );
  }

  if (!agentId) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">หน้านี้ใช้ได้เฉพาะ Agent เจ้าของสายงาน</h1>
          <p className="wx-state-body">
            บัญชีของคุณไม่มีสิทธิ์ Agent เจ้าของสายงานใด หน้านี้ใช้ดูและสร้าง Invite Card ได้เฉพาะ Agent
            เจ้าของสายงานเท่านั้น SuperAdmin, Super Agent และ PLC ดูข้อมูลนี้ได้จากผังสายงานแทน
          </p>
        </div>
      </AppShell>
    );
  }

  if (listStatus === "idle" || (listStatus === "loading" && !listData)) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="cards" label="กำลังโหลดรายการ Invite Card..." />
      </AppShell>
    );
  }

  if (listStatus === "error") {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการ Invite Card ได้</h1>
          <p className="wx-state-body">
            {listError?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ"}
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-ic-page">
        <div className="wx-ic-toolbar">
          <label className="wx-ic-search">
            <span className="wx-visually-hidden">Search invite code, country, note, worker phone</span>
            <input
              type="search"
              placeholder="Search invite code, country, note, worker phone..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="wx-ic-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost" disabled title="ยังไม่เปิดใช้งาน">
              Export
            </button>
          </div>
        </div>

        <div className="wx-ic-head">
          <div className="wx-ic-title">
            <h1>Invite Card ของ Agent</h1>
            <p>
              สร้างลิงก์ใบสมัครให้แรงงานเปิดผ่านเว็บ Workmate X ก่อนรับ mobile app หลัง match งาน
              และก่อนพร้อม match ต้องผ่าน e-KYC เสมอ
            </p>
          </div>
          <div className="wx-ic-head-actions">
            <span className="wx-ic-scope-pill">
              Scope <b>{profile?.agentCode ?? "..."}</b>
              {profile?.displayName ? ` · ${profile.displayName}` : ""}
            </span>
            <button
              type="button"
              className="wx-button wx-button--primary wx-ic-create-trigger"
              onClick={() => setDrawer({ mode: "create" })}
            >
              สร้าง Invite Card
            </button>
          </div>
        </div>

        {/* P157: the QUICK REGISTER total was removed. It counted every
            application the Agent ever received, but this list counts only
            invite cards that are still live - so revoking a card dropped a
            card from the list while its already-registered workers stayed in
            the count, and the two numbers stopped agreeing. The real, always-
            in-sync application list lives at /backoffice/agent/applications;
            this screen is about the cards, not a headline tally. */}
        <div className="wx-ic-metrics">
          <MetricCard label="ACTIVE INVITES" value={metrics.active} note="ยังส่งต่อให้แรงงานได้" variant="green" />
          <MetricCard label="EXPIRED" value={metrics.expired} note="หมดอายุแล้ว ไม่สามารถใช้งานได้" />
        </div>

        <div className="wx-ic-policy">
          <div>
            <strong>{LINK_HIDDEN_FOR_ROLE_TEXT}</strong>
            <span>SuperAdmin, Super Agent และ PLC ดูสถานะได้ แต่จะไม่เห็นรหัสหรือลิงก์คำเชิญของ Agent</span>
          </div>
          <span className="wx-badge wx-badge--success">Agent-only link</span>
        </div>

        <section className="wx-panel wx-ic-list-panel">
          <div className="wx-ic-list-head">
            <h2 className="wx-panel-title">รายการ Invite Card</h2>
            <div className="wx-ic-filters">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`wx-ic-chip${statusFilter === filter ? " wx-ic-chip--active" : ""}`}
                  onClick={() => setStatusFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {items.length === 0 ? (
            <p className="wx-empty-list">ยังไม่มี Invite Card ที่สร้างโดย Agent นี้</p>
          ) : filteredItems.length === 0 ? (
            <p className="wx-empty-list">ไม่พบ Invite Card ที่ตรงกับตัวกรองหรือคำค้นหา</p>
          ) : (
            <div className="wx-ic-table-wrap">
              <table className="wx-ic-table">
                <thead>
                  <tr>
                    <th style={{ width: "18%" }}>Invite</th>
                    <th style={{ width: "17%" }}>Target</th>
                    <th style={{ width: "21%" }}>Public URL</th>
                    <th style={{ width: "13%" }}>Usage</th>
                    <th style={{ width: "14%" }}>Status</th>
                    <th style={{ width: "10%" }}>Expires</th>
                    <th style={{ width: "7%" }} aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((card) => (
                    <tr key={card.inviteCardId} className="wx-ic-row" onClick={() => setDrawer({ mode: "view", card })}>
                      <td>
                        <span className="wx-ic-cell-strong">{card.inviteCodeMasked}</span>
                        <span className="wx-ic-cell-sub">สร้างเมื่อ {formatDateTh(card.createdAtUtc) ?? "-"}</span>
                      </td>
                      <td>
                        <span className="wx-ic-cell-strong">{formatTarget(card)}</span>
                        <span className="wx-ic-cell-sub">{card.noteToWorker || "-"}</span>
                      </td>
                      <td>
                        <PublicUrlCell card={card} />
                      </td>
                      <td>
                        <span className="wx-ic-cell-strong">{formatUsage(card)}</span>
                        <span className="wx-ic-cell-sub">{usageNote(card)}</span>
                      </td>
                      <td>
                        <StatusCell card={card} />
                      </td>
                      <td>
                        <ExpiresCell card={card} />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="wx-ic-row-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDrawer({ mode: "view", card });
                          }}
                        >
                          เปิด
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="wx-ic-pipeline">
            <div className="wx-ic-stage wx-ic-stage--active">
              <strong>1. Invite created</strong>
              <span>Agent ส่งลิงก์เว็บให้แรงงาน ยังไม่ใช่ mobile app</span>
            </div>
            <div className="wx-ic-stage">
              <strong>2. Quick register</strong>
              <span>แรงงานกรอกข้อมูลสำคัญผ่านเว็บ register ยังไม่ถือว่าพร้อม match งาน</span>
            </div>
            <div className="wx-ic-stage">
              <strong>3. e-KYC pending</strong>
              <span>ต้องยืนยันตัวตนก่อนขึ้น ready pool</span>
            </div>
            <div className="wx-ic-stage">
              <strong>4. Ready for match</strong>
              <span>พร้อมให้นายจ้าง match หลัง e-KYC ผ่านเท่านั้น</span>
            </div>
          </div>
        </section>
      </div>

      <DetailDrawer
        open={drawer !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-ic-row, .wx-ic-create-trigger"
      >
        {drawer?.mode === "create" && (
          <CreateInviteCardForm agentId={agentId} onCreated={handleCreated} onClose={closeDrawer} titleId={DRAWER_TITLE_ID} />
        )}
        {drawer?.mode === "view" && (
          <InviteCardInspector agentId={agentId} card={drawer.card} onRevoked={handleRevoked} titleId={DRAWER_TITLE_ID} />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

// Prompt 138Z-A: a blank field means unlimited - the only invalid typed
// value is anything that isn't a positive whole number. Returns null when
// the field is valid (blank or a real positive integer), otherwise the
// Thai error message to show inline.
function validateMaxUses(value) {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return "กรอกจำนวนเต็มบวก หรือเว้นว่างไว้เพื่อไม่จำกัด";
  }
  if (parsed > 1000) {
    return "จำนวนใช้ได้สูงสุดต้องไม่เกิน 1000";
  }
  return null;
}

function CreateInviteCardForm({ agentId, onCreated, onClose, titleId }) {
  const [countryCode, setCountryCode] = useState(COUNTRY_OPTIONS[0].value);
  const [languageCode, setLanguageCode] = useState(LANGUAGE_OPTIONS[0].value);
  // Prompt 138Z-A: blank by default - never "1". Blank genuinely means
  // unlimited usage, not merely "no value chosen yet."
  const [maxUses, setMaxUses] = useState("");
  const [maxUsesError, setMaxUsesError] = useState(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [createdCard, setCreatedCard] = useState(null);

  function handleMaxUsesChange(event) {
    setMaxUses(event.target.value);
    setMaxUsesError(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const validationError = validateMaxUses(maxUses);
    if (validationError) {
      setMaxUsesError(validationError);
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      const created = await createInviteCard(agentId, {
        intendedCountryCode: countryCode || null,
        intendedLanguageCode: languageCode || null,
        noteToWorker: note.trim() ? note.trim() : null,
        // Blank = omit entirely as unlimited (never send 1). A typed value
        // is already validated above as a real positive integer.
        maxUses: maxUses.trim() ? Number(maxUses) : null,
        expiresAtUtc: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setCreatedCard(created);
      onCreated(created);
    } catch (error) {
      setErrorMessage(describeCreateError(error));
    } finally {
      setSubmitting(false);
    }
  }

  if (createdCard) {
    return (
      <div>
        <span className="wx-ic-drawer-kicker">CREATE DRAWER</span>
        <h2 id={titleId} className="wx-ic-drawer-title">
          สร้าง Invite Card สำเร็จ
        </h2>
        <p className="wx-ic-drawer-desc">
          ส่งลิงก์นี้ให้แรงงานเปิดผ่านเว็บ ไม่ใช่ mobile app และยังไม่ถือว่าพร้อม match จนกว่าจะผ่าน e-KYC
        </p>
        <div className="wx-ic-preview">
          <strong>Invite URL</strong>
          {createdCard.canViewInviteLink && createdCard.inviteUrl ? (
            <CopyLinkField url={createdCard.inviteUrl} />
          ) : (
            <span className="wx-ic-preview-url">{LINK_HIDDEN_FOR_ROLE_TEXT}</span>
          )}
        </div>
        <div className="wx-ic-drawer-foot">
          <button type="button" className="wx-button" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <span className="wx-ic-drawer-kicker">CREATE DRAWER</span>
      <h2 id={titleId} className="wx-ic-drawer-title">
        สร้าง Invite Card ใหม่
      </h2>
      <p className="wx-ic-drawer-desc">
        กรอกเฉพาะข้อมูลที่จำเป็นเพื่อสร้างลิงก์เว็บให้แรงงาน ไม่สร้าง worker profile และไม่ถือว่าพร้อม match จนกว่าจะผ่าน e-KYC
      </p>

      <div className="wx-ic-form-row">
        <label className="wx-ic-field">
          <span>ประเทศแรงงาน</span>
          <select value={countryCode} onChange={(event) => setCountryCode(event.target.value)}>
            {COUNTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="wx-ic-field">
          <span>ภาษาเริ่มต้น</span>
          <select value={languageCode} onChange={(event) => setLanguageCode(event.target.value)}>
            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="wx-ic-form-row">
        <label className="wx-ic-field">
          <span>จำนวนใช้ได้สูงสุด</span>
          <input type="number" min="1" max="1000" placeholder="ไม่จำกัด" value={maxUses} onChange={handleMaxUsesChange} />
          <span className="wx-ic-field-hint">เว้นว่างไว้ = ไม่จำกัดจำนวน</span>
          {maxUsesError && <span className="wx-ic-field-error">{maxUsesError}</span>}
        </label>
        <label className="wx-ic-field">
          <span>หมดอายุ (ไม่บังคับ)</span>
          <input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
        </label>
      </div>

      <div className="wx-ic-form-row wx-ic-form-row--single">
        <label className="wx-ic-field">
          <span>Note ถึงแรงงาน (ไม่บังคับ)</span>
          <textarea
            placeholder="เช่น สวัสดีครับ ผม Agent กิตติพงษ์จาก Workmate X ลิงก์นี้ใช้สมัครเบื้องต้นผ่านเว็บเท่านั้น"
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
      </div>

      {errorMessage && (
        <p className="wx-ic-form-message" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="wx-ic-drawer-foot">
        <button type="button" className="wx-button" onClick={onClose} disabled={submitting}>
          ยกเลิก
        </button>
        <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
          {submitting ? "กำลังสร้าง..." : "สร้างและคัดลอกลิงก์"}
        </button>
      </div>
    </form>
  );
}

function InviteCardInspector({ agentId, card, onRevoked, titleId }) {
  const [revoking, setRevoking] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const variant = STATUS_BADGE_VARIANT[card.status] ?? "neutral";

  async function handleRevoke() {
    setRevoking(true);
    setErrorMessage(null);
    try {
      const updated = await revokeInviteCard(agentId, card.inviteCardId);
      onRevoked(updated);
    } catch (error) {
      setErrorMessage(
        error?.status === 500 ? SAFE_CONFIG_ERROR_MESSAGE : "ไม่สามารถเพิกถอน Invite Card ได้ กรุณาลองใหม่อีกครั้ง"
      );
    } finally {
      setRevoking(false);
    }
  }

  return (
    <div>
      <div className="wx-ic-inspector-head">
        <div>
          <span className={`wx-badge wx-badge--${variant}`}>{card.status}</span>
          <h2 id={titleId} className="wx-ic-drawer-title">
            {formatTarget(card)}
          </h2>
          <div className="wx-ic-inspector-code">{card.inviteCodeMasked}</div>
        </div>
      </div>

      {card.canViewInviteLink ? (
        card.status === "Revoked" ? (
          <p className="wx-ic-inspector-note">ลิงก์นี้ถูกเพิกถอนแล้ว ไม่สามารถใช้งานได้อีก แต่ยังเก็บไว้เป็น audit history</p>
        ) : card.inviteUrl ? (
          <div className="wx-ic-preview">
            <strong>Invite URL</strong>
            <CopyLinkField url={card.inviteUrl} />
          </div>
        ) : null
      ) : (
        <p className="wx-ic-inspector-note">{LINK_HIDDEN_FOR_ROLE_TEXT}</p>
      )}

      <div className="wx-kv-grid wx-ic-inspector-kv">
        <div>
          <div className="wx-kv-label">การใช้งาน</div>
          <div className="wx-kv-value">{formatUsage(card)}</div>
        </div>
        <div>
          <div className="wx-kv-label">หมดอายุ</div>
          <div className="wx-kv-value">{formatDateTh(card.expiresAtUtc) ?? "ไม่มีวันหมดอายุ"}</div>
        </div>
        <div>
          <div className="wx-kv-label">สร้างเมื่อ</div>
          <div className="wx-kv-value">{formatDateTh(card.createdAtUtc) ?? "-"}</div>
        </div>
        {card.status === "Revoked" && (
          <div>
            <div className="wx-kv-label">เพิกถอนเมื่อ</div>
            <div className="wx-kv-value">{formatDateTh(card.revokedAtUtc) ?? "-"}</div>
          </div>
        )}
        <div>
          <div className="wx-kv-label">Note ถึงแรงงาน</div>
          <div className="wx-kv-value">{card.noteToWorker || "-"}</div>
        </div>
      </div>

      {errorMessage && (
        <p className="wx-ic-form-message" role="alert">
          {errorMessage}
        </p>
      )}

      {card.canViewInviteLink && card.status !== "Revoked" && (
        <div className="wx-ic-inspector-actions">
          <button type="button" className="wx-button wx-button--ghost" onClick={handleRevoke} disabled={revoking}>
            {revoking ? "กำลังเพิกถอน..." : "เพิกถอน Invite Card"}
          </button>
          {/* Prompt 139C / P157: explicit, calm reassurance right next to the
              action. A card showing "used 250 times" makes an agent fear those
              250 workers vanish with it - so this says in plain words that
              revoking closes only the link, and every worker already registered
              through it stays. No "delete"/scary wording anywhere here. */}
          <p className="wx-ic-inspector-revoke-note">
            การเพิกถอนจะปิดเฉพาะลิงก์นี้ไม่ให้ใช้สมัครใหม่ได้อีก แรงงานที่สมัครผ่านลิงก์นี้ไปแล้วจะยังอยู่ครบในรายการผู้สมัคร ไม่มีการลบข้อมูลแรงงาน
          </p>
        </div>
      )}
    </div>
  );
}
